// src/pages/Mafia/mafiaIdentity.js
//
// Single source of truth for "who is the current Mafia Aux user".
//
// v2: keyed on EmpId. The dashboard runs server-side — there is no Windows
// session in a browser — so we use the logged-in user's EmpId as the stable
// identifier for permission lookups, lock keys, and audit fields. The
// human display name comes along for the ride for UI labels.
//
// Calls GET api/mafia/whoami exactly once per page session and caches the
// result. The backend resolves the X-EmpId header against MafiaAuxPermissions
// (new EmpId column) and empbasic.Name.

import api from "../../api";

let _identityPromise = null;
let _identity = null;

const FALLBACK_IDENTITY = {
  empId: "",
  employeeName: "",
  accessLevel: 0, // 0 = non-credentialed, matches legacy default myPerm
  countryCode: "USA",
  isAdmin: false,
};

/**
 * Fetch (or return cached) Mafia Aux identity for the current user.
 * Always resolves — on failure returns a safe non-credentialed fallback
 * so the menu degrades gracefully rather than crashing.
 *
 * @returns {Promise<{empId: string, employeeName: string, accessLevel: number, countryCode: string, isAdmin: boolean}>}
 */
export async function getMafiaIdentity() {
  if (_identity) return _identity;

  if (!_identityPromise) {
    _identityPromise = api
      // Round MAFIA-Country-1 hotfix 2: hard client timeout — the identity
      // call must never hang behind a slow database; on timeout the safe
      // fallback below applies and the promise cache clears for retry.
      .get("mafia/whoami", { timeout: 20000 })
      .then((response) => {
        const data = response && response.data ? response.data : {};
        _identity = {
          empId:
            typeof data.empId === "string" ? data.empId.trim() : "",
          employeeName:
            typeof data.employeeName === "string" ? data.employeeName.trim() : "",
          accessLevel:
            Number.isFinite(data.accessLevel) && data.accessLevel >= 0
              ? Math.trunc(data.accessLevel)
              : 0,
          countryCode:
            typeof data.countryCode === "string" && data.countryCode
              ? data.countryCode.trim().toUpperCase()
              : "USA",
          isAdmin: data.isAdmin === true,
        };
        return _identity;
      })
      .catch((err) => {
        _identityPromise = null;
        console.error("mafia/whoami failed; using non-credentialed fallback", err);
        return { ...FALLBACK_IDENTITY };
      });
  }

  return _identityPromise;
}

/** Convenience: current user's EmpId — the canonical identifier. */
export async function getMafiaEmpId() {
  const id = await getMafiaIdentity();
  return id.empId;
}

/** Convenience: current user's display name from empbasic.Name. */
export async function getMafiaEmployeeName() {
  const id = await getMafiaIdentity();
  return id.employeeName;
}

/** Convenience: resolved Mafia Aux access level (legacy myPerm). */
export async function getMafiaAccessLevel() {
  const id = await getMafiaIdentity();
  return id.accessLevel;
}

/**
 * Convenience: true when the current user has the legacy `releaser` flag.
 * Per Menu.vb lines 237, 250, 296, 309: releaser = true only when myPerm is
 * 1 (Beta+Full) or 2 (Releasers). All other tiers (3, 4, non-credentialed)
 * are non-releasers.
 *
 * Used by Print page to show/hide the Delete button (Print.vb line 13-19).
 */
export async function getMafiaIsReleaser() {
  const id = await getMafiaIdentity();
  return id.accessLevel === 1 || id.accessLevel === 2;
}

/** Convenience: resolved country/database code. */
export async function getMafiaCountryCode() {
  const id = await getMafiaIdentity();
  return id.countryCode;
}

/** Convenience: true when the current user is an IT Admin. */
export async function getMafiaIsAdmin() {
  const id = await getMafiaIdentity();
  return id.isAdmin === true;
}

/** Clear the cached identity. Call on logout/account switch. */
export function resetMafiaIdentity() {
  _identity = null;
  _identityPromise = null;
}

// ── Round 8 (Batch 2): employee display-name resolution ──────────────────────
//
// MAFIA audit fields (Created By / Last Revised By / Locked By / history) hold a
// MIX of raw identifiers: legacy Windows usernames ("branimir.markovic") and
// modern numeric EmpIds ("3325"). The UI should show human names for both. This
// helper batches the distinct raw values a screen needs into ONE backend call
// (POST mafia/resolve-names) and caches the results, so a 100-row grid never
// fires 100 per-row lookups (the audit's N+1 risk).
//
// Backend returns { rawValue: "First Last (rawValue)" } for values it could
// resolve. Anything unresolved falls back to "Unknown Employee (rawValue)" so a
// name is always shown. Blank stays blank.

const _nameCache = new Map(); // rawValue(lowercased) -> resolved display string

/** Format the fallback shown when a raw id can't be resolved to a name.
 * Round MAFIA-Country-2 fix 3 (owner request): when the identifier itself
 * is a "first.last"-style username, the name is already IN the value — so
 * prettify it ("arsh.koradiya" → "Arsh Koradiya (arsh.koradiya)") instead
 * of labeling a real person "Unknown Employee". Domain prefixes are
 * stripped first ("DOMAIN\\user" → "user"). Purely numeric identifiers
 * (an EmpId with no directory row) can't be humanized, so those keep the
 * explicit Unknown label rather than inventing a name. */
function unknownLabel(raw) {
  const s = String(raw ?? "").trim();
  // Strip a DOMAIN\ prefix if present.
  const bare = s.includes("\\") ? s.slice(s.lastIndexOf("\\") + 1) : s;
  // Humanize only when it looks like a word-based account, not a number.
  if (bare && /[a-zA-Z]/.test(bare) && !/^\d+$/.test(bare)) {
    const pretty = bare
      .split(/[._\-\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
    if (pretty) return `${pretty} (${raw})`;
  }
  return `Unknown Employee (${raw})`;
}

/**
 * Resolve a list of raw identifiers to display names. Returns a Map keyed by the
 * ORIGINAL raw string -> display string. Uses/updates the module cache; only the
 * cache-miss values are sent to the backend. Always resolves (soft-fails to
 * Unknown/raw) so callers never need a try/catch.
 *
 * @param {string[]} rawValues
 * @returns {Promise<Map<string,string>>}
 */
export async function resolveEmployeeNames(rawValues) {
  const out = new Map();
  const misses = [];

  const cleaned = (rawValues || [])
    .map((v) => (v == null ? "" : String(v).trim()))
    .filter((v) => v !== "");

  for (const raw of cleaned) {
    const key = raw.toLowerCase();
    if (_nameCache.has(key)) {
      out.set(raw, _nameCache.get(key));
    } else if (!misses.includes(raw)) {
      misses.push(raw);
    }
  }

  if (misses.length > 0) {
    try {
      const response = await api.post("mafia/resolve-names", misses);
      const map = response && response.data ? response.data : {};
      for (const raw of misses) {
        // Backend keys are case-insensitive; try exact then case-insensitive.
        let resolved = map[raw];
        if (resolved == null) {
          const hit = Object.keys(map).find((k) => k.toLowerCase() === raw.toLowerCase());
          if (hit) resolved = map[hit];
        }
        const display = resolved || unknownLabel(raw);
        _nameCache.set(raw.toLowerCase(), display);
        out.set(raw, display);
      }
    } catch (err) {
      console.error("resolve-names failed; falling back to Unknown/raw", err);
      for (const raw of misses) {
        const display = unknownLabel(raw);
        _nameCache.set(raw.toLowerCase(), display);
        out.set(raw, display);
      }
    }
  }

  return out;
}

/** Synchronous best-effort read of an already-resolved name (or null). */
export function peekResolvedName(raw) {
  if (raw == null) return null;
  const key = String(raw).trim().toLowerCase();
  return _nameCache.has(key) ? _nameCache.get(key) : null;
}

/** Clear the name cache (e.g. on logout). */
export function resetMafiaNameCache() {
  _nameCache.clear();
}
